import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidLinkOnSuccessMetadata, PlaidLinkStableEvent, usePlaidLink } from 'react-plaid-link';
import { ErrorModal } from '@common-fe/common-fe';

import { PendingModal } from '@/components';

import { PlaidSequenceSteps } from '../plaid.types';
import { PlaidDuplicateModal } from '../PlaidDuplicateModal';
import PlaidModal from '../PlaidModal';
import { PlaidPendingAccountModal } from '../PlaidPendingAccountModal';
import { PlaidVerificationFailedModal } from '../PlaidVerificationFailedModal';
import useExchangePublicPlaidTokenQuery from '../queries/useExchangePublicPlaidToken.query';
import useGetPlaidLinkTokenQuery from '../queries/useGetPlaidLinkToken.query';
import useSubmitPlaidBankAccountActivationQuery from '../queries/useSubmitPlaidBankAccountActivation.query';
import { useDuplicatePlaidAccountStore } from '../store';

const PlaidErrorsEnum = {
  TOO_MANY_VERIFICATION_ATTEMPTS: 'TOO_MANY_VERIFICATION_ATTEMPTS',
};

interface Props {
  onExit?: () => void;
  onSuccess?: (metadata: PlaidLinkOnSuccessMetadata) => void | Promise<void>;
  isPendingAccountExists?: boolean;
  accountId?: string;
  isManualVerification?: boolean;
  isTryAgainFlow?: boolean;
}

export const usePlaid = ({
  onExit,
  onSuccess,
  isPendingAccountExists,
  accountId,
  isManualVerification,
  isTryAgainFlow,
}: Props) => {
  const { t } = useTranslation();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isErrorModal, setErrorModal] = useState(false);
  const [verificationFailedModal, setVerificationFailedModal] = useState(false);
  const [isPendingAfterTryAgain, setIsPendingAfterTryAgain] = useState(false);
  const [currentStep, setCurrentStep] = useState<PlaidSequenceSteps | null>(null);
  const { getPlaidLinkToken, isLoading: linkTokenLoading } = useGetPlaidLinkTokenQuery(accountId);
  const { sendPlaidPublicToken, isLoading: publicTokenLoading } = useExchangePublicPlaidTokenQuery();
  const { updateFailedBankStatus } = useSubmitPlaidBankAccountActivationQuery();

  const duplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.isDuplicateModalVisible);
  const setDuplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.setIsDuplicateModalVisible);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      const plaidAccountIds = metadata.accounts?.map((account) => account.id) || [];
      try {
        if (!isManualVerification) {
          await sendPlaidPublicToken({
            public_token,
            plaid_account_ids: plaidAccountIds,
          });
        }
        const currentAccountStatus = metadata?.accounts[0]?.verification_status;
        if (isTryAgainFlow && currentAccountStatus && currentAccountStatus.split('_').includes('pending')) {
          setIsPendingAfterTryAgain(true);
        }
        await onSuccess?.(metadata);
      } catch {
        setErrorModal(true);
      } finally {
        setCurrentStep(null);
      }
    },
    onEvent: (eventName, metadata) => {
      if (eventName === PlaidLinkStableEvent.EXIT) {
        if (onExit) {
          onExit();
        }
        if (metadata?.error_code === PlaidErrorsEnum.TOO_MANY_VERIFICATION_ATTEMPTS) {
          updateFailedBankStatus({ elevate_account_id: accountId as string });
          setVerificationFailedModal(true);
        }
      }
    },
    onLoad: () => {
      if (currentStep === PlaidSequenceSteps.link_token) {
        return setCurrentStep(PlaidSequenceSteps.iframe);
      }
      onExit?.();
    },
  });

  const getLinkToken = useCallback(async () => {
    setCurrentStep(PlaidSequenceSteps.link_token);
    if (isPendingAccountExists) {
      return;
    }
    const token = await getPlaidLinkToken();
    setLinkToken(token);
  }, [getPlaidLinkToken, isPendingAccountExists]);

  const content = useMemo(() => {
    if (duplicateAccountModal) {
      return (
        <PlaidDuplicateModal
          visible
          onSetVisible={() => {
            setDuplicateAccountModal(false);
            onExit?.();
          }}
          onSubmit={() => {
            setDuplicateAccountModal(false);
            onExit?.();
          }}
        />
      );
    }
    if (isPendingAccountExists && currentStep === PlaidSequenceSteps.link_token) {
      return (
        <PlaidPendingAccountModal
          visible
          onSetVisible={() => {
            setCurrentStep(null);
            onExit?.();
          }}
          onSubmit={() => {
            setCurrentStep(null);
            onExit?.();
          }}
        />
      );
    }

    if (currentStep === PlaidSequenceSteps.link_token) {
      return (
        <PlaidModal
          visible
          onSetVisible={() => {
            setCurrentStep(null);
            onExit?.();
          }}
          isLoading
        />
      );
    }

    if (isErrorModal) {
      return (
        <ErrorModal
          testId="plaid-error-modal"
          visible
          onSetVisible={setErrorModal}
          header={t('Something went wrong')}
          helpText={t('Try again later or contact support')}
          buttonText={t('Got It!')}
          onSubmit={() => setErrorModal(false)}
        />
      );
    }

    if (verificationFailedModal) {
      return (
        <PlaidVerificationFailedModal
          visible
          onSetVisible={() => {
            setVerificationFailedModal(false);
            onExit?.();
          }}
          onSubmit={() => {
            setVerificationFailedModal(false);
            onExit?.();
          }}
        />
      );
    }

    if (isPendingAfterTryAgain) {
      return (
        <PendingModal
          header={t('Your request has been submitted for additional review.')}
          buttonText={t('Got It!')}
          visible
          onSetVisible={() => {
            setIsPendingAfterTryAgain(false);
            onExit?.();
          }}
        />
      );
    }

    return null;
  }, [
    currentStep,
    duplicateAccountModal,
    setDuplicateAccountModal,
    isErrorModal,
    isPendingAccountExists,
    verificationFailedModal,
    isPendingAfterTryAgain,
    onExit,
    t,
  ]);

  useEffect(() => {
    if (linkToken && ready && currentStep === PlaidSequenceSteps.iframe) {
      open();
    }
  }, [linkToken, ready, open, currentStep]);

  return {
    content,
    openPlaid: getLinkToken,
    isLoading: linkTokenLoading || publicTokenLoading,
  };
};
